// hooks/useLanguage.ts
import { useContext, useMemo } from "react";
import LanguageContext, { LanguageContextType } from "../contexts/languageContext";

// Fallback language object
const createFallbackLanguage = () => ({
  code: 'en',
  label: 'English',
  direction: 'ltr' as const,
});

// Fallback context value when provider is not available
const fallbackContextValue = {
  languages: [createFallbackLanguage()],
  currentLanguage: createFallbackLanguage(),
  isLoading: false,
  error: null,
  isFallbackActive: true,
  changeLanguage: (langCode: string) => {
    console.warn(`useLanguage: changeLanguage called but LanguageProvider is not available. Language code: ${langCode}`);
  },
  fetchLanguages: async () => {
    console.warn('useLanguage: fetchLanguages called but LanguageProvider is not available.');
  },
  direction: 'ltr' as const,
  isRtl: false,
  isLtr: true,
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  
  // If context is undefined, provider is not available - use fallback
  if (context === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        'useLanguage: LanguageProvider is not available. Using fallback values. ' +
        'To enable full language functionality, wrap your app with <ERPUIProvider> or <LanguageProvider>.'
      );
    }
    
    return fallbackContextValue;
  }
  
  const computedValues = useMemo(() => {
    const direction = context.currentLanguage?.direction || 'ltr';
    const isRtl = direction === 'rtl';
    const isLtr = direction === 'ltr';
    
    return { direction, isRtl, isLtr };
  }, [context.currentLanguage]);
  
  return {
    ...context,
    ...computedValues,
  };
};
//  const withLanguage = <P extends object>(
//   Component: React.ComponentType<P>
// ) => {
//   return (props: P) => {
//     const languageContext = useLanguage();
//     return <Component {...props} languageContext={languageContext} />;
//   };
// };

